import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { RaskRoller } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IMottagningsplats } from "~app/features/forsaljning/mottagningsplats/mottagningsplats.models";
import { VirkesorderHandelssortimentDTO } from "../../common/models/virkesorder/VirkesorderHandelssortimentDTO";
import { ApiClientService } from "../../common/services/api-client.service";
import { CFlisArbetsorderVirkesorderDto } from "../Leverans/extern-arbetsorder/models/CFlisArbetsorderVirkesorderDto";
import { IArbetsorderVirkesorder } from "./virkesorder.models";

@Injectable({
    providedIn: "root"
})

export class VirkesorderService {

    private readonly apiClient = inject(ApiClientService);
    private readonly authenticationService = inject(AuthenticationService);

    canSend(isDirty: boolean, pending: boolean, skickad: Date, antalAktivaVirkesorderrader: number): boolean {
        if (!this.authenticationService.hasRole(RaskRoller.role.Produktionsleda)) {
            return false;
        }

        if (isDirty && !pending) {
            if (ObjectUtils.hasValue(skickad) && antalAktivaVirkesorderrader < 60) {
                return true;
            }

            if (!ObjectUtils.hasValue(skickad) && antalAktivaVirkesorderrader < 50) {
                return true;
            }
        }

        return false;
    }

    createCanSendVirkesorderFunc = (ao: IArbetsorderVirkesorder) => (): boolean => {
        if (!this.authenticationService.hasRole(RaskRoller.role.Produktionsleda)) {
            return false;
        }

        if (ao.isViol2 && (
            ao.rules.hasActiveValsVo() ||
            !ao.rules.hasHandelssortiment() ||
            ao.rules.isViol2Disabled() ||
            ao.rules.hasVirkesorderradsBegransning()
        )) {
            return false;
        }

        return ao.rules.areKoordinaterVerifierade() &&
            !ao.rules.hasValidationMessages() &&
            ao.rules.isProduktsatt() &&
            ao.rules.isVoContractDirty() &&
            !ao.rules.isPending() &&
            !ao.rules.isArbetsorderDirty() &&
            ao.rules.isSchemalagd() &&
            ao.rules.atgarderIsTillgangliga();
    };

    getCFlisArbetsorderVirkesorderrader(virkesorderNr: string): Observable<CFlisArbetsorderVirkesorderDto> {
        return this.apiClient.get<CFlisArbetsorderVirkesorderDto>(`virkesorder/GetCFlisArbetsorderVirkesorderrader`, false, { virkesorderNr: virkesorderNr });
    }

    getMottagningsplatser(): Observable<IMottagningsplats[]> {
        return this.apiClient.get<IMottagningsplats[]>(`virkesorder/GetMottagningsplatser`, false);
    }

    getSendVoErrorText(skickad: Date, antalAktivaVirkesorderrader: number): string {
        if (ObjectUtils.hasValue(skickad) && antalAktivaVirkesorderrader > 60) {
            return `Virkesordern har ${antalAktivaVirkesorderrader} rader, dessa måste begränsas till max 60 rader`;
        }

        if (!ObjectUtils.hasValue(skickad) && antalAktivaVirkesorderrader > 50) {
            return `Virkesordern har ${antalAktivaVirkesorderrader} rader, dessa måste begränsas till max 50 rader`;
        }

        return undefined;
    }

    getVirkesorder(virkesorderNr: string): Observable<VirkesorderHandelssortimentDTO> {
        return this.apiClient.get<VirkesorderHandelssortimentDTO>(`virkesorder/${virkesorderNr}`);
    }

    saveCFlisArbetsorderVirkesorderrader(aovo: CFlisArbetsorderVirkesorderDto): Observable<any> {
        return this.apiClient.post<CFlisArbetsorderVirkesorderDto, any>(`virkesorder/SaveCFlisArbetsorderVirkesorderrader`, aovo, false);
    }

    showSendVoError(skickad: Date, antalAktivaVirkesorderrader: number): boolean {
        if (ObjectUtils.hasValue(skickad) && antalAktivaVirkesorderrader > 60) {
            return true;
        }

        if (!ObjectUtils.hasValue(skickad) && antalAktivaVirkesorderrader > 50) {
            return true;
        }

        return false;
    }
}
